import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import FooterV2 from "@/components/Home/v2/FooterV2";
import NavbarV2 from "@/components/Home/v2/NavbarV2";
import Text from "@/components/Home/v2/shared/Text";
import GraphyHead from "@/components/organisms/GraphyHead";
import { homeV2Data } from "@/shared/data/home.v2.data";
import Image from "next/image";
import Link from "next/link";

const RECENT_RELEASES = [
  {
    label: "Multiple Memberships & Recurring Subscription",
    href: "/updates/multiple-memberships",
    date: "May 2025",
    active: true,
  },
];

function Sidebar() {
  return (
    <aside className="w-full lg:w-64 lg:flex-shrink-0">
      <div className="sticky top-24">
        <p className="mb-3 font-inter text-[11px] font-semibold uppercase tracking-widest text-darkSlate04">
          Latest Releases
        </p>
        <ul className="flex flex-col gap-1">
          {RECENT_RELEASES.map((r) => (
            <li key={r.href}>
              <Link
                href={r.href}
                className={`block rounded-lg px-3 py-2.5 transition-colors ${
                  r.active
                    ? "bg-brandPrimary/8 font-semibold text-brandPrimary"
                    : "text-darkSlate03 hover:bg-neutralGrey hover:text-neutralBlack"
                }`}
              >
                <span className="font-inter text-[13px] leading-snug">
                  {r.label}
                </span>
                <span className="mt-0.5 block font-inter text-[11px] text-darkSlate04">
                  {r.date}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function SectionImage({
  src,
  alt,
}: {
  src: string;
  alt: string;
}) {
  return (
    <div className="my-6 overflow-hidden rounded-2xl border border-[#E8E8E8] shadow-sm">
      <Image
        src={src}
        alt={alt}
        width={1440}
        height={900}
        className="w-full object-cover"
      />
    </div>
  );
}

function Divider() {
  return <hr className="my-10 border-[#EBEBEB]" />;
}

export default function MultipleMembershipsPage() {
  return (
    <div className="home-v2-scope min-h-screen bg-white">
      <GraphyHead
        title="Multiple Memberships & Recurring Subscription — Graphy Updates"
        description="Creators can now create multiple memberships and offer recurring autopay subscriptions in India."
        canonicalPath="/updates/multiple-memberships"
        countryCode=""
      />

      <NavbarV2 />

      {/* ── Hero ── */}
      <section className="w-full bg-[#F7F5FF] pb-10 pt-16 md:pt-20">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6">
          <Link
            href="/updates"
            className="mb-5 inline-flex items-center gap-1.5 font-inter text-[13px] font-medium text-brandPrimary hover:underline"
          >
            ← Graphy Updates
          </Link>

          <div className="mb-3 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-brandPrimary/10 px-3 py-1 font-inter text-[11px] font-semibold uppercase tracking-widest text-brandPrimary">
              Feature Launch
            </span>
            <span className="font-inter text-[13px] text-darkSlate04">
              May 2025
            </span>
          </div>

          <div className="mb-3 text-[40px] leading-none">🥁</div>

          <Text as="h1" variant="display-md" className="text-neutralBlack mb-4 max-w-2xl">
            Multiple Memberships &amp; Recurring Subscription
          </Text>

          <Text as="p" variant="body-lg" className="max-w-2xl text-darkSlate04">
            We&apos;ve rolled out a major upgrade to memberships. Creators can now
            create multiple memberships and offer recurring autopay subscriptions
            in India.
          </Text>
        </div>
      </section>

      {/* ── Body ── */}
      <section className="w-full bg-white py-14 md:py-20">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">

            {/* ── Main content ── */}
            <article className="min-w-0 flex-1">

              {/* Multiple memberships */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Multiple memberships
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Creators can now create as many memberships as they want. Each
                membership has its own courses, pricing plans, FAQs, and a
                shared editable landing page template — making it easy to run
                tiered offerings, topic-specific bundles, or audience-specific
                memberships in parallel.
              </Text>
              <SectionImage
                src="/images/updates/memberships-dashboard.png"
                alt="Memberships dashboard showing membership cards with member count and revenue"
              />

              <Divider />

              {/* Recurring autopay */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Recurring autopay for Indian creators
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Indian creators can now enable autopay on their membership plans
                via Cashfree. Learners no longer need to manually renew every
                cycle — subscriptions renew automatically on the billing date.
                Learners can cancel anytime; cancellation takes effect on the
                next renewal date. Stripe (US) continues to support autopay as
                before. Razorpay autopay is out of scope for this release.
              </Text>

              <Divider />

              {/* Flexible pricing */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Flexible pricing
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-2">
                Each membership supports up to 5 pricing plans with three plan
                types: Free, OTP, and Subscription. Subscription intervals
                available are monthly, quarterly, yearly, and custom.
              </Text>

              <Divider />

              {/* Pause & archive */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Pause &amp; archive states
              </Text>
              <ul className="mb-4 flex flex-col gap-3">
                {[
                  { label: "Pause", desc: "Stops new sign-ups. Existing subscribers continue unaffected." },
                  { label: "Archive", desc: "No new purchases or renewals. Existing subscribers continue till their next renewal date." },
                ].map(({ label, desc }) => (
                  <li key={label} className="flex gap-3">
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brandPrimary/10 font-inter text-[10px] font-bold text-brandPrimary">
                      {label[0]}
                    </span>
                    <Text as="p" variant="body-lg" className="text-darkSlate03">
                      <strong className="font-semibold text-neutralBlack">{label}:</strong>{" "}
                      {desc}
                    </Text>
                  </li>
                ))}
              </ul>

              <Divider />

              {/* Customers tab */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Customers tab per membership
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-2">
                Each membership now has its own Customers tab to view
                subscribers, with quick actions to View Learner and View Plan.
              </Text>

              <Divider />

              {/* Promo codes */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Promo code support
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-2">
                Memberships are now a product type in promo code creation.
                Creators can search and apply codes to specific memberships, with
                the option to apply to new memberships only.
              </Text>

              <Divider />

              {/* Campaigns filter */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Campaigns filter updates
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Two new audience filters are available in Campaigns: <strong className="font-semibold text-neutralBlack">Enrolled in Memberships</strong> and{" "}
                <strong className="font-semibold text-neutralBlack">Not Enrolled in Memberships</strong>. The existing membership status filter is unchanged.
              </Text>
              <SectionImage
                src="/images/updates/campaigns-membership-filter.png"
                alt="Campaigns filter panel showing the new Enrolled in membership(s) and Not enrolled in membership(s) filters"
              />

              <Divider />

              {/* Reports */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Reports → Transactions
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-2">
                Memberships are now a supported product type under Transactions.
                Creators can filter transaction reports by specific memberships.
              </Text>

              <Divider />

              {/* Users / Learners */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Users → Learners filter updates
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-2">
                Two new filters added in the Learners view: <strong className="font-semibold text-neutralBlack">Enrolled in Memberships</strong> and{" "}
                <strong className="font-semibold text-neutralBlack">Not Enrolled in Memberships</strong>. The existing membership status filter is unchanged.
              </Text>

              <Divider />

              {/* Website editor */}
              <Text as="h2" variant="title-lg" className="text-neutralBlack mb-3">
                Website editor changes
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Both the membership store page and the membership landing page
                template are now visible in the website editor. The store page
                works like other store pages — creators need to update the
                membership link in the header if they&apos;ve already added it.
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                For the landing page template, creators should delete existing
                sections and re-add them so the new multi-plan layout takes
                effect. Hero section behaviour: a single-plan membership shows
                the Buy button with price in the hero directly; multiple plans
                show a pricing section and the hero CTA reads{" "}
                <em>&ldquo;Choose a plan&rdquo;</em>.
              </Text>
              <SectionImage
                src="/images/updates/memberships-landing-page-editor.png"
                alt="Membership landing page editor showing name, description, FAQs and cover image upload"
              />

              <Divider />

              {/* Notes */}
              <div className="rounded-2xl border border-[#FDEDC5] bg-[#FFFBF0] p-6">
                <Text as="h3" variant="title-sm" className="text-neutralBlack mb-4">
                  Notes
                </Text>
                <ol className="flex flex-col gap-3">
                  {[
                    "The Membership checkbox in the course pricing tab has been removed. Courses can now only be added to a membership from Memberships → Edit Membership → Add Courses.",
                    "The old membership will remain visible on the membership store page.",
                  ].map((note, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-inter text-[13px] font-bold text-[#B8860B]">
                        {i + 1}.
                      </span>
                      <Text as="p" variant="body-md" className="text-darkSlate03">
                        {note}
                      </Text>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-8 rounded-2xl border border-[#E8E8E8] bg-[#F9F9F9] p-6">
                <Text as="h3" variant="title-sm" className="text-neutralBlack mb-4">
                  Scope of this release
                </Text>
                <ul className="flex flex-col gap-2.5">
                  {[
                    "Web only",
                    "Android and iOS support coming in subsequent releases",
                    'No changes to the learner experience in this release; learners will continue to see courses that are part of a membership with an "Included in membership" tag on the course cards',
                    "In the next release, we'll add a dedicated Memberships tab on the learner side where learners can view all memberships they've purchased individually",
                    "Allow autopay on Razorpay",
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#AAAAAA]" />
                      <Text as="p" variant="body-md" className="text-darkSlate04">
                        {item}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>

            </article>

            {/* ── Sidebar ── */}
            <Sidebar />

          </div>
        </div>
      </section>

      <CtaBannerV2 {...homeV2Data.ctaBanner} />
      <FooterV2 {...homeV2Data.footer} />
    </div>
  );
}
